import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db, auth } from '../lib/firebase';
import { 
    collection, 
    query, 
    where, 
    onSnapshot, 
    orderBy, 
    doc, 
    deleteDoc, 
    addDoc, 
    serverTimestamp, 
    updateDoc, 
    getDocs 
} from 'firebase/firestore';
import { useAuth } from '../lib/AuthContext';
import LZString from 'lz-string';
import { 
    getGradeFromRisk, 
    deduplicateViolations,
    getComplianceLabel,
    VIOLATION_COSTS
} from '../lib/parseResults';
import ScanResultsView from '../components/ScanResultsView';
import { RiskScoreTrendTremor, SeverityTrendTremor, WcagCriteriaBreakdownNivo } from '../components/AnalyticsCharts';
import { ConfirmModal, Toast } from '../components/InAppNotification';
import UpgradePromptModal from '../components/UpgradePromptModal';

const STATUS_STYLES = {
    pending: { label: 'In Queue', bg: 'bg-gray-500/10', text: 'text-gray-500', icon: '⏳' },
    processing: { label: 'Scrambling...', bg: 'bg-(--accent)/10', text: 'text-(--accent)', icon: '⚡' },
    completed: { label: 'Analyzed', bg: 'bg-(--success)/10', text: 'text-(--success)', icon: '💎' },
    failed: { label: 'Error', bg: 'bg-(--error)/10', text: 'text-(--error)', icon: '🚨' },
};

const SitesPage = ({ initialSite, onIssueSelect, onRescan }) => {
    const { user, planLimits } = useAuth();
    const [scans, setScans] = useState([]);
    const [hydratedResults, setHydratedResults] = useState({});
    const [isLoadingChunks, setIsLoadingChunks] = useState(false);
    const [sites, setSites] = useState([]); // Will be derived from scans but keeping name for compatibility
    const [activeDomain, setActiveDomain] = useState(initialSite || null);
    const [activeTab, setActiveTab] = useState('remediation');
    const [searchQuery, setSearchQuery] = useState('');
    const [sitesSort, setSitesSort] = useState({ key: 'lastScan', direction: 'desc' });
    const [isSitesSortOpen, setIsSitesSortOpen] = useState(false);
    
    // Remediation Tracker State
    const [remediationSearch, setRemediationSearch] = useState('');
    const [remediationSeverityFilter, setRemediationSeverityFilter] = useState([]);
    const [remediationPolicyFilter, setRemediationPolicyFilter] = useState([]);
    const [remediationSort, setRemediationSort] = useState({ key: 'impact', direction: 'desc' });
    const [selectedIssues, setSelectedIssues] = useState(new Set());
    const [isSeverityDropdownOpen, setIsSeverityDropdownOpen] = useState(false);
    const [isPolicyDropdownOpen, setIsPolicyDropdownOpen] = useState(false);
    
    // Scan History State
    const [historySearch, setHistorySearch] = useState('');
    const [historySort, setHistorySort] = useState({ key: 'date', direction: 'desc' });
    const [historyDateRange, setHistoryDateRange] = useState('all'); // 'all', '7d', '30d', '90d', 'custom'
    const [historyCustomStart, setHistoryCustomStart] = useState('');
    const [historyCustomEnd, setHistoryCustomEnd] = useState('');
    const [selectedScans, setSelectedScans] = useState(new Set());
    const [expandedScan, setExpandedScan] = useState(null);
    const [confirmState, setConfirmState] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
    const [toast, setToast] = useState(null);
    const [deleteConfirmScanId, setDeleteConfirmScanId] = useState(null);
    
    // Pages State
    const [pagesSearchQuery, setPagesSearchQuery] = useState('');
    const [pagesRuleFilter, setPagesRuleFilter] = useState('');
    const [pagesSort, setPagesSort] = useState({ key: 'url', direction: 'asc' });
    const [pagesSeverityFilter, setPagesSeverityFilter] = useState([]);
    const [pagesPolicyFilter, setPagesPolicyFilter] = useState([]);
    const [isPagesSeverityDropdownOpen, setIsPagesSeverityDropdownOpen] = useState(false);
    const [isPagesPolicyDropdownOpen, setIsPagesPolicyDropdownOpen] = useState(false);
    const [pagesPerPage, setPagesPerPage] = useState(10);
    const [pagesCurrentPage, setPagesCurrentPage] = useState(1);
    
    // Reports State
    const [reports, setReports] = useState([]);
    const [reportsSearch, setReportsSearch] = useState('');
    const [reportsSort, setReportsSort] = useState({ key: 'date', direction: 'desc' });
    const [viewingReportId, setViewingReportId] = useState(null);
    const [isGenerateReportModalOpen, setIsGenerateReportModalOpen] = useState(false);
    const [newReportName, setNewReportName] = useState('');
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    const [downloadingPDF, setDownloadingPDF] = useState(null);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isHydratingSite, setIsHydratingSite] = useState(false);
    const [upgradeModal, setUpgradeModal] = useState({ isOpen: false, title: '', message: '' });

    // Click outside handler for custom dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.custom-dropdown')) {
                setIsSeverityDropdownOpen(false);
                setIsPolicyDropdownOpen(false);
                setIsPagesSeverityDropdownOpen(false);
                setIsPagesPolicyDropdownOpen(false);
                setIsSitesSortOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Initial Site Load from Prop
    useEffect(() => {
        if (initialSite) setActiveDomain(initialSite);
    }, [initialSite]);

    // Data Listeners
    useEffect(() => {
        if (!user) return;
        // Fetch from user's scan queue instead of global sites collection
        const q = query(collection(db, 'users', user.uid, 'queue'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const scansData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setScans(scansData);
        }, (err) => {
            console.error("[Sites] Queue listener error:", err);
        });
        return () => unsubscribe();
    }, [user]);

    // Derive Unique Sites from Scans
    useEffect(() => {
        const siteMap = new Map();
        scans.forEach(scan => {
            if (!scan.url || scan.status !== 'completed') return;
            try {
                const hostname = new URL(scan.url).hostname;
                const existing = siteMap.get(hostname);
                
                // Add scan to site's scan list
                const siteScans = existing ? [...existing.scans, scan] : [scan];
                
                // Track latest scan for primary metrics
                const latestScan = !existing || (scan.createdAt?.seconds || 0) > (existing.latestScan?.createdAt?.seconds || 0) 
                    ? scan 
                    : existing.latestScan;

                siteMap.set(hostname, {
                    id: hostname, // Use hostname as ID for consistency
                    domain: hostname,
                    latestScan,
                    scans: siteScans,
                    summary: latestScan.summary || {}
                });
            } catch (e) {
                console.warn("[Sites] Failed to parse domain from scan:", scan.url);
            }
        });
        
        const derivedSites = Array.from(siteMap.values()).sort((a, b) => {
            const timeA = Math.min(...a.scans.map(s => s.createdAt?.seconds || Infinity));
            const timeB = Math.min(...b.scans.map(s => s.createdAt?.seconds || Infinity));
            return timeA - timeB;
        });

        const maxDomains = planLimits?.maxDomains || 1;
        const processedSites = derivedSites.map((site, index) => ({
            ...site,
            isLocked: maxDomains !== -1 && index >= maxDomains
        }));

        setSites(processedSites);
    }, [scans, planLimits]);

    useEffect(() => {
        if (!user || !activeDomain) return;
        const q = query(
            collection(db, 'users', user.uid, 'reports'), 
            where('domain', '==', activeDomain),
            orderBy('createdAt', 'desc')
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const reportsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setReports(reportsData);
        }, (error) => {
            console.error("[Sites] Reports listener error:", error);
        });
        return () => unsubscribe();
    }, [user, activeDomain]);

    // Derived Data
    const filteredSites = useMemo(() => {
        const filtered = sites.filter(site => 
            site.domain.toLowerCase().includes(searchQuery.toLowerCase())
        );

        return [...filtered].sort((a, b) => {
            const direction = sitesSort.direction === 'asc' ? 1 : -1;
            
            if (sitesSort.key === 'alphabetical') {
                return direction * a.domain.localeCompare(b.domain);
            }
            if (sitesSort.key === 'risk') {
                const scoreA = a.latestScan?.summary?.riskScore || 0;
                const scoreB = b.latestScan?.summary?.riskScore || 0;
                // Risk sorting defaults to desc (highest risk first)
                return direction * (scoreA - scoreB);
            }
            if (sitesSort.key === 'lastScan') {
                const timeA = a.latestScan?.createdAt?.seconds || 0;
                const timeB = b.latestScan?.createdAt?.seconds || 0;
                // Last scan sorting defaults to desc (newest first)
                return direction * (timeA - timeB);
            }
            return 0;
        });
    }, [sites, searchQuery, sitesSort]);

    // Auto-select site if only one is listed
    useEffect(() => {
        if (filteredSites.length === 1 && filteredSites[0].domain !== activeDomain) {
            setActiveDomain(filteredSites[0].domain);
        }
    }, [filteredSites, activeDomain]);

    const activeSite = useMemo(() => {
        return sites.find(s => s.domain === activeDomain);
    }, [sites, activeDomain]);
    // Helper to extract result data from a scan (handles chunks, inline compressed, and raw)
    const getScanResults = useCallback((scan) => {
        if (!scan) return null;
        
        // 1. Check hydratedResults (from chunks or already decompressed)
        if (hydratedResults[scan.id]) return hydratedResults[scan.id];
        if (scan.hydratedResults) return scan.hydratedResults;

        // 2. Check inline results
        const raw = scan.results;
        if (!raw) return null;

        if (typeof raw === 'string' && scan.dataIsCompressed) {
            try {
                return JSON.parse(LZString.decompressFromUTF16(raw));
            } catch (e) {
                console.error("[Sites] Failed to decompress inline results for scan:", scan.id, e);
                return null;
            }
        }
        
        return raw;
    }, [hydratedResults]);

    // Hydrate scan results for the active site
    useEffect(() => {
        if (!activeSite || !user) return;

        const unscannedScans = activeSite.scans.filter(s => 
            s.status === 'completed' && 
            s.hasChunks && 
            !hydratedResults[s.id]
        );

        if (unscannedScans.length === 0) return;

        const hydrate = async () => {
            setIsLoadingChunks(true);
            try {
                for (const scan of unscannedScans) {
                    const chunksRef = collection(db, 'users', user.uid, 'queue', scan.id, 'chunks');
                    const q = query(chunksRef, orderBy('index', 'asc'));
                    const snapshot = await getDocs(q);

                    if (snapshot.empty) continue;

                    const fullData = snapshot.docs
                        .map(doc => doc.data().data)
                        .join('');

                    let parsedResults;
                    if (scan.dataIsCompressed) {
                        const decompressed = LZString.decompressFromUTF16(fullData);
                        parsedResults = JSON.parse(decompressed);
                    } else {
                        parsedResults = JSON.parse(fullData);
                    }

                    setHydratedResults(prev => ({
                        ...prev,
                        [scan.id]: parsedResults
                    }));
                }
            } catch (err) {
                console.error("[Sites] Failed to hydrate scan chunks:", err);
            } finally {
                setIsLoadingChunks(false);
            }
        };

        hydrate();
    }, [activeSite, user, hydratedResults]);

    // Aggregate Metrics (Current Backlog)
    const aggregateMetrics = useMemo(() => {
        if (!activeSite || !activeSite.scans) return {
            riskScore: 0,
            totalRiskCost: 0,
            totalViolations: 0,
            totalAffectedNodes: 0,
            totalBacklogSize: 0,
            uniquePageCount: 0
        };
        
        // Aggregate all unique issues across all scans for this domain
        const allViolations = activeSite.scans.flatMap(s => {
            const results = getScanResults(s);
            return results?.violations || [];
        });
        const summary = deduplicateViolations([{ violations: allViolations }]);
        
        if (!summary) return {
            riskScore: 0,
            totalRiskCost: 0,
            totalViolations: 0,
            totalAffectedNodes: 0,
            totalBacklogSize: 0,
            uniquePageCount: 0
        };

        // Determine unique pages
        const pages = new Set();
        activeSite.scans.forEach(s => {
            if (s.url) pages.add(s.url);
            if (s.seedUrls) s.seedUrls?.forEach(u => pages.add(u));
            if (s.summary?.ruleStats?.pages) {
                Object.keys(s.summary.ruleStats.pages).forEach(u => pages.add(u));
            }
        });

        return {
            riskScore: summary.riskScore || 0,
            totalRiskCost: summary.totalRiskCost || 0,
            totalViolations: summary.totalViolations || 0,
            totalAffectedNodes: summary.totalAffectedNodes || 0,
            totalBacklogSize: summary.totalAffectedNodes || 0,
            uniquePageCount: pages.size
        };
    }, [activeSite, hydratedResults]);

    const trendData = useMemo(() => {
        if (!activeSite?.scans) return [];
        
        // Sort scans chronologically
        const chronScans = [...activeSite.scans]
            .filter(s => s.status === 'completed' && s.summary)
            .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));

        return chronScans.map(s => ({
            date: s.createdAt?.toDate ? s.createdAt.toDate() : new Date((s.createdAt?.seconds || 0) * 1000),
            riskScore: s.summary?.riskScore || 0,
            severities: s.summary?.bySeverity || { critical: 0, serious: 0, moderate: 0, minor: 0 }
        }));
    }, [activeSite]);

    const criteriaBreakdown = useMemo(() => {
        if (!activeSite || !activeSite.scans) return [];
        
        const latestScan = [...activeSite.scans]
            .filter(s => s.status === 'completed' && s.summary)
            .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))[0];

        if (!latestScan || !latestScan.summary) return [];

        const ruleStats = latestScan.summary.ruleStats;
        if (ruleStats) {
            return Object.entries(ruleStats)
                .map(([ruleId, data]) => ({
                    criteria: data.title || ruleId.replace(/-/g, ' ').toUpperCase(),
                    count: data.count || 0
                }))
                .sort((a, b) => a.count - b.count)
                .slice(-10);
        }

        // Fallback to hydrated results if ruleStats is somehow missing
        const results = getScanResults(latestScan);
        if (!results || !results.violations) return [];

        const criteriaMap = {};
        results.violations.forEach(v => {
            const ruleTitle = v.id.replace(/-/g, ' ').toUpperCase();
            criteriaMap[ruleTitle] = (criteriaMap[ruleTitle] || 0) + (v.nodes?.length || 1);
        });

        return Object.entries(criteriaMap)
            .map(([criteria, count]) => ({ criteria, count }))
            .sort((a, b) => a.count - b.count)
            .slice(-10);
    }, [activeSite, hydratedResults, getScanResults]);

    const dedupedActiveSiteBacklog = useMemo(() => {
        if (!activeSite || !activeSite.scans) return { violations: [], summary: null };

        const allViolations = activeSite.scans.flatMap(s => {
            const raw = getScanResults(s);
            if (!raw) return [];
            
            // Shallow clone and normalize nodes for deduplication
            const normalized = {
                ...raw,
                violations: (raw.violations || []).map(v => ({
                    ...v,
                    nodes: (v.nodes || []).map(n => ({
                        ...n,
                        pageUrl: s.url || n.pageUrl
                    }))
                }))
            };
            return [normalized];
        });

        const summary = deduplicateViolations(allViolations);
        if (!summary || !summary.violations) return { violations: [], summary };

        const groups = new Map();
        summary.violations.forEach(v => {
            if (!groups.has(v.violationId)) {
                groups.set(v.violationId, {
                    ruleId: v.violationId,
                    description: v.description,
                    impact: v.impact,
                    tags: v.tags || [],
                    costPerInstance: VIOLATION_COSTS[v.impact] || 0,
                    nodes: []
                });
            }
            groups.get(v.violationId).nodes.push(v.node);
        });

        return {
            violations: Array.from(groups.values()),
            summary
        };
    }, [activeSite, hydratedResults]);

    const filteredAndSortedIssues = useMemo(() => {
        let result = [...(dedupedActiveSiteBacklog?.violations || [])];

        if (remediationSearch) {
            result = result.filter(v => 
                v.ruleId.toLowerCase().includes(remediationSearch.toLowerCase()) || 
                (v.description && v.description.toLowerCase().includes(remediationSearch.toLowerCase()))
            );
        }

        if (remediationSeverityFilter.length > 0) {
            result = result.filter(v => remediationSeverityFilter.includes(v.impact));
        }

        if (remediationPolicyFilter.length > 0) {
            result = result.filter(v => 
                v.tags && v.tags.some(tag => remediationPolicyFilter.includes(tag))
            );
        }

        result.sort((a, b) => {
            const getImpactWeight = (impact) => {
                switch(impact) {
                    case 'critical': return 4;
                    case 'serious': return 3;
                    case 'moderate': return 2;
                    case 'minor': return 1;
                    default: return 0;
                }
            };
            let valA, valB;
            switch(remediationSort.key) {
                case 'impact':
                    valA = getImpactWeight(a.impact);
                    valB = getImpactWeight(b.impact);
                    break;
                case 'ruleId':
                    valA = a.ruleId.toLowerCase();
                    valB = b.ruleId.toLowerCase();
                    break;
                case 'instances':
                    valA = a.nodes?.length || 0;
                    valB = b.nodes?.length || 0;
                    break;
                default:
                    valA = a[remediationSort.key] || 0;
                    valB = b[remediationSort.key] || 0;
            }
            return remediationSort.direction === 'asc' ? 
                (valA < valB ? -1 : 1) : 
                (valA > valB ? -1 : 1);
        });

        return result;
    }, [dedupedActiveSiteBacklog, remediationSearch, remediationSort, remediationSeverityFilter, remediationPolicyFilter]);

    // Tab Filters
    const filteredAndSortedScans = useMemo(() => {
        if (!activeSite?.scans) return [];
        let result = [...activeSite.scans];
        
        if (historySearch) {
            result = result.filter(s => 
                s.id.toLowerCase().includes(historySearch.toLowerCase()) ||
                (s.summary?.status || '').toLowerCase().includes(historySearch.toLowerCase())
            );
        }

        result.sort((a, b) => {
            let valA, valB;
            switch(historySort.key) {
                case 'date':
                    valA = a.createdAt?.seconds || 0;
                    valB = b.createdAt?.seconds || 0;
                    break;
                case 'score':
                    valA = a.summary?.riskScore || 0;
                    valB = b.summary?.riskScore || 0;
                    break;
                case 'violations':
                    valA = a.summary?.totalViolations || 0;
                    valB = b.summary?.totalViolations || 0;
                    break;
                default:
                    valA = a[historySort.key];
                    valB = b[historySort.key];
            }
            return historySort.direction === 'asc' ? 
                (valA < valB ? -1 : 1) : 
                (valA > valB ? -1 : 1);
        });

        return result;
    }, [activeSite, historySearch, historySort]);

    const pagesData = useMemo(() => {
        if (!activeSite?.scans) return [];
        const pageStats = new Map();

        // Build an overarching map of Rule ID -> Tags (Policies) from all hydrated data to enrich ruleStats
        const globalRuleTags = new Map();
        activeSite.scans.forEach(scan => {
            const raw = getScanResults(scan);
            if (raw && raw.violations) {
                raw.violations.forEach(v => {
                    const ruleId = v.id || v.violationId;
                    if (v.tags && ruleId) {
                        if (!globalRuleTags.has(ruleId)) globalRuleTags.set(ruleId, new Set());
                        v.tags.forEach(t => globalRuleTags.get(ruleId).add(t));
                    }
                });
            }
        });

        activeSite.scans.forEach(scan => {
            if (scan.status !== 'completed' || !scan.url) return;
            const summary = scan.summary || {};

            let pagesFromRuleStats = new Set();
            if (summary?.ruleStats) {
                Object.values(summary.ruleStats).forEach(rule => {
                    if (rule.pages && typeof rule.pages === 'object') {
                        Object.keys(rule.pages).forEach(url => pagesFromRuleStats.add(url));
                    }
                });
            }

            if (pagesFromRuleStats.size > 0) {
                pagesFromRuleStats.forEach(pageUrl => {
                    let totalViolations = 0;
                    const bySeverity = { critical: 0, serious: 0, moderate: 0, minor: 0 };
                    let totalRiskCost = 0;
                    const affectedRules = new Set();
                    const tags = new Set();

                    Object.entries(summary.ruleStats).forEach(([ruleId, rule]) => {
                        const count = rule.pages?.[pageUrl] || 0;
                        if (count > 0) {
                            totalViolations += count;
                            const imp = rule.impact || 'minor';
                            if (Object.prototype.hasOwnProperty.call(bySeverity, imp)) {
                                bySeverity[imp] += count;
                            }
                            totalRiskCost += (VIOLATION_COSTS[imp] || 0) * count;
                            affectedRules.add(ruleId);
                            const ruleTags = globalRuleTags.get(ruleId);
                            if (ruleTags) ruleTags.forEach(t => tags.add(t));
                        }
                    });

                    const riskScore = Math.min(100, bySeverity.critical * 10 + bySeverity.serious * 5 + bySeverity.moderate * 2 + bySeverity.minor);
                    const existing = pageStats.get(pageUrl);
                    if (!existing || (scan.createdAt?.seconds || 0) > (existing.lastScan || 0)) {
                        pageStats.set(pageUrl, {
                            url: pageUrl,
                            totalViolations,
                            bySeverity,
                            riskScore,
                            totalRiskCost,
                            lastScan: scan.createdAt?.seconds || 0,
                            scanId: scan.id,
                            affectedRules: Array.from(affectedRules),
                            tags: Array.from(tags)
                        });
                    }
                });
            } else {
                let raw = hydratedResults[scan.id] || scan.hydratedResults || scan.results;
                if (typeof raw === 'string' && scan.dataIsCompressed) {
                    try { raw = JSON.parse(LZString.decompressFromUTF16(raw)); } catch {}
                }

                if (raw) {
                    const pageViolations = {};
                    let targetViolations = raw.violations;
                    const pageResults = [];
                    if (Array.isArray(raw)) {
                        raw.forEach(entry => {
                            const v = entry.results?.violations || entry.violations;
                            if (v) pageResults.push({ url: entry.url || scan.url, violations: v });
                        });
                    } else if (targetViolations) {
                        pageResults.push({ url: scan.url, violations: targetViolations });
                    }

                    pageResults.forEach(({ url: defaultUrl, violations }) => {
                        violations.forEach(v => {
                            const impact = v.impact || 'minor';
                            const ruleId = v.id || v.violationId || v.ruleId;
                            
                            let nodeFound = false;
                            (v.nodes || []).forEach(node => {
                                const url = node.pageUrl || defaultUrl;
                                nodeFound = true;
                                if (!pageViolations[url]) {
                                    pageViolations[url] = { total: 0, bySeverity: { critical: 0, serious: 0, moderate: 0, minor: 0 }, cost: 0, rules: new Set(), tags: new Set() };
                                }
                                pageViolations[url].total += 1;
                                pageViolations[url].bySeverity[impact] += 1;
                                pageViolations[url].cost += VIOLATION_COSTS[impact] || 0;
                                if (ruleId) pageViolations[url].rules.add(ruleId);
                                
                                const ruleTags = globalRuleTags.get(ruleId);
                                if (ruleTags) ruleTags.forEach(t => pageViolations[url].tags.add(t));
                                else if (v.tags) v.tags.forEach(t => pageViolations[url].tags.add(t));
                            });

                            if (!nodeFound && v.pageUrls) {
                                v.pageUrls.forEach(url => {
                                    if (!pageViolations[url]) {
                                        pageViolations[url] = { total: 0, bySeverity: { critical: 0, serious: 0, moderate: 0, minor: 0 }, cost: 0, rules: new Set(), tags: new Set() };
                                    }
                                    const countPerPage = Math.ceil((v.nodes?.length || 1) / v.pageUrls.length);
                                    pageViolations[url].total += countPerPage;
                                    pageViolations[url].bySeverity[impact] += countPerPage;
                                    pageViolations[url].cost += (VIOLATION_COSTS[impact] || 0) * countPerPage;
                                    if (ruleId) pageViolations[url].rules.add(ruleId);
                                    
                                    const ruleTags = globalRuleTags.get(ruleId);
                                    if (ruleTags) ruleTags.forEach(t => pageViolations[url].tags.add(t));
                                    else if (v.tags) v.tags.forEach(t => pageViolations[url].tags.add(t));
                                });
                            }
                            
                            if (!nodeFound && (!v.pageUrls || v.pageUrls.length === 0)) {
                                const url = defaultUrl;
                                if (!pageViolations[url]) {
                                    pageViolations[url] = { total: 0, bySeverity: { critical: 0, serious: 0, moderate: 0, minor: 0 }, cost: 0, rules: new Set(), tags: new Set() };
                                }
                                pageViolations[url].total += 1;
                                pageViolations[url].bySeverity[impact] += 1;
                                pageViolations[url].cost += VIOLATION_COSTS[impact] || 0;
                                if (ruleId) pageViolations[url].rules.add(ruleId);
                                
                                const ruleTags = globalRuleTags.get(ruleId);
                                if (ruleTags) ruleTags.forEach(t => pageViolations[url].tags.add(t));
                                else if (v.tags) v.tags.forEach(t => pageViolations[url].tags.add(t));
                            }
                        });
                    });

                    Object.entries(pageViolations).forEach(([pageUrl, stats]) => {
                        const riskScore = Math.min(100, stats.bySeverity.critical * 10 + stats.bySeverity.serious * 5 + stats.bySeverity.moderate * 2 + stats.bySeverity.minor);
                        const existing = pageStats.get(pageUrl);
                        if (!existing || (scan.createdAt?.seconds || 0) > (existing.lastScan || 0)) {
                            pageStats.set(pageUrl, {
                                url: pageUrl,
                                totalViolations: stats.total,
                                bySeverity: stats.bySeverity,
                                riskScore,
                                totalRiskCost: stats.cost,
                                lastScan: scan.createdAt?.seconds || 0,
                                scanId: scan.id,
                                affectedRules: Array.from(stats.rules),
                                tags: Array.from(stats.tags)
                            });
                        }
                    });
                } else {
                    const existing = pageStats.get(scan.url);
                    if (!existing || (scan.createdAt?.seconds || 0) > (existing.lastScan || 0)) {
                        pageStats.set(scan.url, {
                            url: scan.url,
                            totalViolations: summary.totalViolations || 0,
                            bySeverity: summary.bySeverity || { critical: 0, serious: 0, moderate: 0, minor: 0 },
                            riskScore: summary.riskScore || 0,
                            totalRiskCost: summary.totalRiskCost || 0,
                            lastScan: scan.createdAt?.seconds || 0,
                            affectedRules: [],
                            tags: []
                        });
                    }
                }
            }
        });

        // Ensure we add pages that were found but might not have violations (0 score)
        activeSite.scans.forEach(scan => {
            const extraUrls = new Set();
            if (scan.seedUrls) scan.seedUrls.forEach(u => extraUrls.add(u));
            if (scan.foundPages) scan.foundPages.forEach(u => extraUrls.add(u));
            
            extraUrls.forEach(url => {
                if (!pageStats.has(url)) {
                    pageStats.set(url, {
                        url,
                        totalViolations: 0,
                        bySeverity: { critical: 0, serious: 0, moderate: 0, minor: 0 },
                        riskScore: 0,
                        totalRiskCost: 0,
                        lastScan: scan.createdAt?.seconds || 0,
                        scanId: scan.id,
                        affectedRules: [],
                        tags: []
                    });
                }
            });
        });

        let result = Array.from(pageStats.values());

        if (pagesSearchQuery) {
            result = result.filter(p => p.url.toLowerCase().includes(pagesSearchQuery.toLowerCase()));
        }

        if (pagesSeverityFilter.length > 0) {
            result = result.filter(p => {
                const bySeverity = p.bySeverity;
                if (!bySeverity) return false;
                return pagesSeverityFilter.some(sev => bySeverity[sev] > 0);
            });
        }

        if (pagesPolicyFilter.length > 0) {
            result = result.filter(p => {
                const tags = p.tags || [];
                return pagesPolicyFilter.some(pol => tags.includes(pol));
            });
        }

        if (pagesRuleFilter) {
            result = result.filter(p => p.affectedRules && p.affectedRules.includes(pagesRuleFilter));
        }

        result.sort((a, b) => {
            let valA, valB;
            if (pagesSort.key === 'lastScan') {
                valA = a.lastScan || 0;
                valB = b.lastScan || 0;
            } else if (pagesSort.key === 'riskScore') {
                valA = a.riskScore || 0;
                valB = b.riskScore || 0;
            } else if (pagesSort.key === 'totalViolations') {
                valA = a.totalViolations || 0;
                valB = b.totalViolations || 0;
            } else {
                valA = a[pagesSort.key] || '';
                valB = b[pagesSort.key] || '';
                if (typeof valA === 'string' && typeof valB === 'string') {
                    return pagesSort.direction === 'desc' ? valB.localeCompare(valA) : valA.localeCompare(valB);
                }
            }
            return pagesSort.direction === 'desc' ? valB - valA : valA - valB;
        });

        return result;
    }, [activeSite, pagesSearchQuery, pagesSort, pagesSeverityFilter, pagesRuleFilter, pagesPolicyFilter, hydratedResults]);

    const paginatedPages = useMemo(() => {
        if (pagesPerPage === 'All') return pagesData;
        const start = (pagesCurrentPage - 1) * pagesPerPage;
        return pagesData.slice(start, start + pagesPerPage);
    }, [pagesData, pagesCurrentPage, pagesPerPage]);

    // Actions
    const handleTargetedRescan = async (ruleIdOrIds) => {
        if (!activeDomain || !user) return;

        const ruleIds = Array.isArray(ruleIdOrIds) ? ruleIdOrIds : [ruleIdOrIds];
        const allViolations = activeSite.scans.flatMap(s => hydratedResults[s.id] || s.hydratedResults || []);

        // Find URLs that have these specific rule violations
        const targetUrls = new Set();
        allViolations.forEach(v => {
            if (ruleIds.includes(v.violationId)) {
                if (v.pageUrl || v.url) targetUrls.add(v.pageUrl || v.url);
            }
        });

        if (targetUrls.size === 0) targetUrls.add(activeDomain);

        try {
            await addDoc(collection(db, 'users', user.uid, 'queue'), {
                url: activeDomain,
                seedUrls: Array.from(targetUrls),
                config: {
                    complianceType: 'Targeted Verification',
                    maxPages: targetUrls.size,
                    depth: 0,
                    targetedRules: ruleIds
                },
                status: 'pending',
                createdAt: serverTimestamp(),
                userId: user.uid
            });
            setSelectedIssues(new Set());
            // Navigate to scans tab to show it queued
            setActiveTab('scans');
            setToast({ message: 'Targeted verification scan queued.', type: 'success' });
        } catch (error) {
            console.error("Targeted rescan failed:", error);
            setToast({ message: 'Targeted scan failed to queue.', type: 'error' });
        }
    };

    const handleRetryScan = async (scanId) => {
        try {
            await updateDoc(doc(db, 'users', user.uid, 'queue', scanId), {
                status: 'pending',
                error: null,
                updatedAt: serverTimestamp()
            });
            setToast({ message: 'Scan queued for retry.', type: 'info' });
        } catch (err) {
            console.error('Error retrying scan:', err);
            setToast({ message: 'Failed to retry scan.', type: 'error' });
        }
    };

    const handleCancelScan = (scanId, isProcessing = false) => {
        setConfirmState({
            isOpen: true,
            title: isProcessing ? 'Stop active scan' : 'Cancel Scan',
            message: isProcessing
                ? 'Are you sure you want to stop this active scan? It will be marked as failed.'
                : 'Are you sure you want to permanently delete this pending scan?',
            onConfirm: async () => {
                try {
                    if (isProcessing) {
                        await updateDoc(doc(db, 'users', user.uid, 'queue', scanId), {
                            status: 'failed',
                            error: 'Scan stopped by user',
                            updatedAt: serverTimestamp()
                        });
                        setToast({ message: 'Stopping scan...', type: 'info' });
                    } else {
                        await deleteDoc(doc(db, 'users', user.uid, 'queue', scanId));
                        setToast({ message: 'Scan cancelled.', type: 'info' });
                    }
                } catch (err) {
                    console.error('Error cancelling scan:', err);
                }
                setConfirmState(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleArchiveScan = async (scanId, archiveState) => {
        try {
            await updateDoc(doc(db, 'users', user.uid, 'queue', scanId), {
                archived: archiveState,
                updatedAt: serverTimestamp(),
            });
            setToast({ message: `Scan ${archiveState ? 'archived' : 'restored'}.`, type: 'info' });
        } catch (err) {
            console.error('Error archiving scan:', err);
        }
    };

    const handlePermanentDelete = (scanId) => {
        setDeleteConfirmScanId(scanId);
    };

    const sortedReports = useMemo(() => {
        let result = [...reports];
        
        if (reportsSearch) {
            result = result.filter(r => 
                r.name.toLowerCase().includes(reportsSearch.toLowerCase())
            );
        }

        result.sort((a, b) => {
            let valA, valB;
            switch(reportsSort.key) {
                case 'date':
                    valA = a.createdAt?.seconds || 0;
                    valB = b.createdAt?.seconds || 0;
                    break;
                case 'name':
                    valA = (a.name || '').toLowerCase();
                    valB = (b.name || '').toLowerCase();
                    break;
                case 'risk':
                    valA = a.summary?.riskScore || 0;
                    valB = b.summary?.riskScore || 0;
                    break;
                default:
                    valA = a[reportsSort.key] || 0;
                    valB = b[reportsSort.key] || 0;
            }
            return reportsSort.direction === 'asc' ? 
                (valA < valB ? -1 : 1) : 
                (valA > valB ? -1 : 1);
        });

        return result;
    }, [reports, reportsSearch, reportsSort]);

    const confirmPermanentDelete = async (scanId) => {
        try {
            await deleteDoc(doc(db, 'users', user.uid, 'queue', scanId));
            setToast({ message: 'Scan permanently deleted.', type: 'info' });
        } catch (err) {
            console.error('Error deleting scan:', err);
        }
        setDeleteConfirmScanId(null);
    };

    const cancelDelete = () => {
        setDeleteConfirmScanId(null);
    };

    const handleToggleSelect = (e, scanId) => {
        e.stopPropagation();
        const next = new Set(selectedScans);
        if (next.has(scanId)) next.delete(scanId);
        else next.add(scanId);
        setSelectedScans(next);
    };

    const handleRescan = async (domain) => {
        if (onRescan) onRescan(domain);
    };

    const handleDeleteSite = async () => {
        if (!activeDomain || isDeleting) return;
        setIsDeleting(true);
        try {
            const siteDoc = sites.find(s => s.domain === activeDomain);
            if (siteDoc && siteDoc.scans) {
                const batch = [];
                siteDoc.scans.forEach(scan => {
                    batch.push(deleteDoc(doc(db, 'users', user.uid, 'queue', scan.id)));
                });
                await Promise.all(batch);
                setActiveDomain(null);
                setShowDeleteModal(false);
            }
        } catch (err) {
            console.error('Delete Site Error:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSelectAll = (scans) => {
        if (selectedScans.size === scans.length) setSelectedScans(new Set());
        else setSelectedScans(new Set(scans.map(s => s.id)));
    };

    const handleGenerateReport = async () => {
        if (!newReportName.trim() || isGeneratingReport) return;
        setIsGeneratingReport(true);
        try {
            // Aggregate results for the selected site
            const allViolations = activeSite.scans.flatMap(s => hydratedResults[s.id] || s.hydratedResults || []);
            const summary = deduplicateViolations(allViolations);

            await addDoc(collection(db, 'users', user.uid, 'reports'), {
                name: newReportName,
                domain: activeDomain,
                hydratedResults: summary?.violations || [],
                summary: aggregateMetrics,
                createdAt: serverTimestamp(),
                userId: user.uid
            });
            setIsGenerateReportModalOpen(false);
            setNewReportName('');
        } catch (err) {
            console.error('Generate Report Error:', err);
        } finally {
            setIsGeneratingReport(false);
        }
    };

    const handleDeleteReport = async (reportId) => {
        try {
            await deleteDoc(doc(db, 'users', user.uid, 'reports', reportId));
        } catch (err) {
            console.error('Delete Report Error:', err);
        }
    };

    const handleDownloadPDF = async (report) => {
        setDownloadingPDF(report.id);
        try {
            // PDF generation logic via cloud function
            const md = `# Audit Report: ${report.name}\n\n...`;
            const response = await fetch('https://us-central1-a11y-scanner-c052f.cloudfunctions.net/generatePDFReportAuth', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ markdown: md, userId: user.user.uid })
            });

            if (!response.ok) throw new Error('PDF Generation failed');

            const blob = await response.blob();
            const downloadLink = document.createElement('a');
            downloadLink.href = URL.createObjectURL(blob);
            downloadLink.download = `${report.name.replace(/\s+/g, '_')}_Report.pdf`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        } catch (err) {
            console.error('PDF Download Error:', err);
            alert('Failed to download PDF.');
        } finally {
            setDownloadingPDF(null);
        }
    };

    const calculateEstimatedTime = (scan) => {
        const scanStatus = scan.summary?.status || scan.status;
        
        if (scanStatus === 'pending') {
            const maxPages = scan.maxPages || 50;
            const deviceCount = (scan.devices || ['desktop']).length;
            // Rough estimate: 8s per page-device for "Initial Estimate"
            const totalEstimatedSeconds = maxPages * deviceCount * 8;
            if (totalEstimatedSeconds < 60) return `~${totalEstimatedSeconds}s`;
            return `~${Math.round(totalEstimatedSeconds / 60)}m`;
        }

        if (scanStatus === 'processing' && scan.progress) {
            const { crawled, total, scanStartedAt } = scan.progress;
            if (!crawled || !total || !scanStartedAt || crawled < 2) return 'Estimating...';

            const elapsedMs = Date.now() - (scanStartedAt.seconds ? scanStartedAt.seconds * 1000 : scanStartedAt);
            const remaining = total - crawled;
            if (remaining <= 0) return 'Finishing...';

            const msPerPage = elapsedMs / crawled;
            const remainingMs = remaining * msPerPage;

            if (remainingMs < 10000) return 'A few seconds...';
            
            const totalSeconds = Math.round(remainingMs / 1000);
            if (totalSeconds < 60) return `~${totalSeconds}s`;
            
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            return `~${minutes}m ${seconds}s`;
        }

        return null;
    };

    const renderScanRow = (scan) => {
        const isExpanded = expandedScan === scan.id;
        const scanStatus = scan.summary?.status || scan.status;
        const status = STATUS_STYLES[scanStatus] || STATUS_STYLES.pending;

        return (
            <React.Fragment key={scan.id}>
                <tr className="hover:bg-teal/5 transition-colors group">
                    <td className="p-5 text-center">
                        <input
                            type="checkbox"
                            checked={selectedScans.has(scan.id)}
                            onChange={(e) => handleToggleSelect(e, scan.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 cursor-pointer accent-teal"
                        />
                    </td>
                    <td className="p-5">
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-espresso break-all max-w-[18.75em]" title={scan.url}>
                                    {(() => {
                                        if (scan.seedUrls) {
                                            try {
                                                return `${new URL(scan.url).hostname} [Cluster: ${scan.seedUrls.length}]`;
                                            } catch (e) {
                                                return scan.displayUrl || scan.url;
                                            }
                                        }
                                        return scan.displayUrl || scan.url;
                                    })()}
                                </span>
                                <span className={`px-2 py-0.5 rounded text-[0.5rem] font-black uppercase tracking-widest border flex items-center gap-1 ${status.bg} ${status.text} border-${status.text.split('-')[1]}/30 whitespace-nowrap`}>
                                    {status.icon} {status.label}
                                </span>
                            </div>
                            <span className="text-[0.5625rem] font-black text-espresso/40 uppercase tracking-widest">
                                {getComplianceLabel(scan.complianceType || scan.config?.complianceType || 'federal')} • {scan.createdAt ? new Date(scan.createdAt.seconds * 1000).toLocaleString() : 'N/A'}
                            </span>
                        </div>
                    </td>
                    <td className="p-5 text-center">
                        <span className={`text-[0.5625rem] font-black border px-2 py-0.5 rounded ${scan.summary?.riskScore > 0 ? 'border-(--fail-red)/30 text-(--fail-red) bg-(--fail-red)/10' : 'border-(--pass-green)/30 text-(--pass-green) bg-(--pass-green)/10'}`}>
                            {scan.summary?.riskScore === 0 ? 'CLEAN' : `SCORE: ${scan.summary?.riskScore || 0}`}
                        </span>
                    </td>
                    <td className="p-5 text-center text-teal font-black text-xs">
                        {scan.summary?.totalViolations || 0}
                    </td>
                    <td className="p-5 border-l border-espresso/5 bg-stone/5">
                        <div className="flex flex-col items-center justify-center gap-2 min-h-[4rem]">
                            <div className="flex items-center gap-2">
                                {scanStatus === 'failed' && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleRetryScan(scan.id); }}
                                        className="p-1 px-2 text-[0.5625rem] font-black uppercase tracking-widest bg-(--warn-amber)/10 text-(--warn-amber) hover:bg-(--warn-amber)/20 rounded border border-(--warn-amber)/20 transition-colors"
                                        title="Retry failed scan"
                                    >
                                        Retry
                                    </button>
                                )}

                                {(scanStatus === 'pending' || scanStatus === 'processing') && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCancelScan(scan.id, scanStatus === 'processing');
                                        }}
                                        className="p-1 px-2 text-[0.5625rem] font-black uppercase tracking-widest bg-stone/50 hover:bg-(--fail-red)/10 text-(--fail-red) rounded border border-espresso/10 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                )}

                                {scanStatus === 'completed' && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleArchiveScan(scan.id, !scan.archived);
                                        }}
                                        className="p-1 px-2 text-[0.5625rem] font-black uppercase tracking-widest bg-stone/50 hover:bg-stone text-espresso/60 rounded border border-espresso/10 transition-colors"
                                    >
                                        {scan.archived ? 'Restore' : 'Archive'}
                                    </button>
                                )}

                                {deleteConfirmScanId === scan.id ? (
                                    <div className="flex items-center gap-1 bg-(--fail-red)/10 rounded px-1 border border-(--fail-red)/20">
                                        <span className="text-(--fail-red) font-black text-[0.5rem] uppercase px-1">Del?</span>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); confirmPermanentDelete(scan.id); }}
                                            className="p-0.5 px-1.5 bg-(--fail-red)/20 hover:bg-(--fail-red)/30 text-(--fail-red) font-black text-[0.5rem] uppercase rounded transition-colors"
                                        >
                                            Yes
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); cancelDelete(); }}
                                            className="p-0.5 px-1.5 bg-stone/50 hover:bg-stone text-espresso/60 font-black text-[0.5rem] uppercase rounded transition-colors"
                                        >
                                            No
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handlePermanentDelete(scan.id); }}
                                        className="p-1 px-2 text-[0.5625rem] font-black uppercase tracking-widest bg-(--fail-red)/5 hover:bg-(--fail-red)/10 text-(--fail-red) rounded border border-(--fail-red)/10 transition-colors"
                                    >
                                        Delete
                                    </button>
                                )}
                            </div>
                            
                            <button 
                                onClick={(e) => { e.stopPropagation(); setExpandedScan(isExpanded ? null : scan.id); }}
                                className="w-full px-2 py-1 text-[0.5625rem] font-black uppercase tracking-widest bg-teal/5 text-teal hover:bg-teal/10 rounded border border-teal/20 transition-all whitespace-nowrap"
                            >
                                {isExpanded ? 'Close Details' : 'Expand Details'}
                            </button>
                        </div>
                        {(scanStatus === 'processing' || scanStatus === 'pending') && (
                            <div className="mt-2 space-y-1 w-full">
                                {scanStatus === 'processing' && scan.progress && (
                                    <div className="w-full bg-stone/50 rounded-full h-1 overflow-hidden">
                                        <div
                                            className="h-full bg-teal transition-all duration-500"
                                            style={{ width: `${Math.min(100, ((scan.progress.crawled || 0) / Math.max(1, scan.progress.total || 1)) * 100)}%` }}
                                        />
                                    </div>
                                )}
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-teal text-[0.5rem] font-black tracking-widest uppercase flex items-center gap-1">
                                        <span className="animate-pulse">⏳</span> 
                                        {scanStatus === 'processing' ? `${scan.progress?.crawled || 0}/${scan.progress?.total || 0}` : 'IN QUEUE'}
                                    </p>
                                    <p className="text-espresso/40 text-[0.45rem] font-black tracking-widest uppercase text-right">
                                        {scanStatus === 'pending' ? 'Est: ' : 'Rem: '}
                                        <span className="text-teal/60">{calculateEstimatedTime(scan)}</span>
                                    </p>
                                </div>
                            </div>
                        )}
                    </td>
                </tr>
                {isExpanded && (
                    <tr>
                        <td colSpan="5" className="p-0 bg-stone/5 border-b border-espresso/10 border-t-0 shadow-inner">
                            <div className="p-6 animate-in slide-in-from-top-2 duration-300">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-8 h-8 rounded-full bg-teal/10 text-teal flex items-center justify-center border border-teal/20 text-xs">ℹ️</div>
                                    <div>
                                        <h4 className="font-black text-espresso/80 uppercase tracking-widest text-[0.625rem]">Diagnostics Summary & Logs</h4>
                                        <p className="text-espresso/40 text-[0.5625rem] uppercase tracking-widest font-bold">Detailed violation report for {scan.url}</p>
                                    </div>
                                </div>
                                <div className="bg-white border border-espresso/10 rounded-xl overflow-hidden shadow-sm max-h-[31.25em] overflow-y-auto">
                                    {getScanResults(scan) ? (
                                        <ScanResultsView
                                            results={getScanResults(scan)}
                                            scanConfig={scan.config}
                                            isEmbedded={true}
                                        />
                                    ) : (
                                        <div className="p-12 text-center bg-stone/5 flex flex-col items-center justify-center h-48">
                                            <div className="text-2xl opacity-20 mb-3 grayscale">📊</div>
                                            <p className="text-[0.625rem] font-black uppercase tracking-widest text-espresso/40">Loading detailed diagnostics telemetry...</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        );
    };

    return (
        <div className="flex h-screen bg-warm-pearl">
            {/* Sidebar: Site List */}
            <div className="w-80 border-r border-espresso/10 bg-stone/20 flex flex-col">
                <div className="p-6 border-b border-espresso/10">
                    <h2 className="text-xl font-black uppercase tracking-tighter text-espresso mb-4">Sites</h2>
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <input
                                type="text"
                                placeholder="Search domains..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-white border border-espresso/10 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-teal transition-all"
                            />
                            <span className="absolute right-3 top-2.5 opacity-30">🔍</span>
                        </div>
                        <div className="relative custom-dropdown">
                            <button
                                onClick={() => setIsSitesSortOpen(!isSitesSortOpen)}
                                className={`h-full px-3 flex items-center justify-center bg-white border border-espresso/10 rounded-xl hover:border-teal transition-all ${isSitesSortOpen ? 'border-teal ring-2 ring-teal/10' : ''}`}
                                title="Sort Sites"
                            >
                                <span className="opacity-50 text-xs">⇅</span>
                            </button>
                            
                            {isSitesSortOpen && (
                                <div className="absolute right-0 mt-2 w-48 bg-white border border-espresso/10 rounded-xl shadow-xl z-50 p-2 space-y-1 animate-in fade-in zoom-in-95 duration-200">
                                    <p className="text-[0.625rem] font-black uppercase tracking-widest text-espresso/40 px-2 py-1 mb-1">Sort Sites By</p>
                                    {[
                                        { key: 'lastScan', label: 'Last Scan', icon: '🕒' },
                                        { key: 'alphabetical', label: 'Alphabetical', icon: 'A-Z' },
                                        { key: 'risk', label: 'Risk Score', icon: '🚨' }
                                    ].map(option => (
                                        <button
                                            key={option.key}
                                            onClick={() => {
                                                setSitesSort(prev => ({
                                                    key: option.key,
                                                    direction: prev.key === option.key ? (prev.direction === 'asc' ? 'desc' : 'asc') : (option.key === 'alphabetical' ? 'asc' : 'desc')
                                                }));
                                                setIsSitesSortOpen(false);
                                            }}
                                            className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-between ${
                                                sitesSort.key === option.key 
                                                ? 'bg-teal/5 text-teal' 
                                                : 'text-espresso/70 hover:bg-stone/50'
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="opacity-50 text-[10px] w-6">{option.icon}</span>
                                                {option.label}
                                            </div>
                                            {sitesSort.key === option.key && (
                                                <span className="text-[10px]">{sitesSort.direction === 'asc' ? '↑' : '↓'}</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto no-scrollbar p-2 space-y-1">
                    {filteredSites.map(site => (
                        <button
                            key={site.domain}
                            onClick={() => !site.isLocked && setActiveDomain(site.domain)}
                            className={`w-full text-left px-4 py-3 rounded-xl transition-all group ${site.isLocked ? 'opacity-50 cursor-not-allowed bg-stone/20' : (activeDomain === site.domain
                                ? 'bg-teal text-white shadow-md'
                                : 'hover:bg-stone/50 text-espresso/70'
                                )}`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="font-bold text-xs truncate uppercase tracking-widest">{site.domain}</div>
                                {site.isLocked && <span className="text-[0.625rem] bg-(--fail-red) text-white px-1.5 py-0.5 rounded-full shadow-sm ml-2">LOCKED</span>}
                            </div>
                            <div className={`text-[0.625rem] mt-1 font-bold ${site.isLocked ? 'opacity-40 text-espresso/50' : (activeDomain === site.domain ? 'text-white/80' : 'text-teal/80')}`}>
                                {site.scans?.length || 0} Scans • Risk: {site.latestScan?.summary?.riskScore || 0}
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Main: Domain Detail */}
            <div className="flex-1 overflow-y-auto no-scrollbar">
                {activeSite ? (
                    <div className="p-10 space-y-10 animate-in fade-in slide-in-from-right-4 duration-500">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-10">
                            <div className="flex-1">
                                <h1 className="text-4xl font-black text-espresso tracking-tighter uppercase mb-2">
                                    {activeSite.domain}
                                </h1>
                                <div className="flex items-center gap-3">
                                    <p className="text-xs font-bold text-teal tracking-[0.3em] uppercase opacity-60">Site Compliance Intelligence</p>
                                    {isLoadingChunks && (
                                        <div className="flex items-center gap-2 px-2 py-0.5 bg-teal/5 border border-teal/20 rounded-full animate-pulse">
                                            <span className="w-1.5 h-1.5 bg-teal rounded-full animate-bounce"></span>
                                            <span className="text-[0.5625rem] font-black text-teal uppercase tracking-widest">Hydrating Intelligence...</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <button
                                    onClick={() => handleRescan(activeSite.domain)}
                                    className="px-6 py-3 bg-teal text-white rounded-full font-bold text-xs uppercase tracking-widest shadow-lg hover:opacity-90 transition-all"
                                >
                                    ⚡ Rescan Domain
                                </button>
                                <button
                                    onClick={() => setShowDeleteModal(true)}
                                    className="px-6 py-3 rounded-full border border-(--fail-red)/30 text-(--fail-red) font-bold text-xs uppercase tracking-widest hover:bg-(--fail-red)/5 transition-all"
                                >
                                    🗑️ Delete Site
                                </button>
                            </div>
                        </div>

                        {/* Top Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bento-card flex items-center justify-between p-8">
                                <div>
                                    <p className="text-[0.625rem] font-black uppercase tracking-widest text-espresso/40 mb-2">Compliance Grade</p>
                                    <div className="text-5xl font-black text-espresso">
                                        {getGradeFromRisk(aggregateMetrics?.riskScore || 0).grade}
                                    </div>
                                </div>
                                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl border-2 ${getGradeFromRisk(aggregateMetrics?.riskScore || 0).pass ? 'border-(--pass-green) text-(--pass-green) bg-(--pass-green)/5' : 'border-(--fail-red) text-(--fail-red) bg-(--fail-red)/5'}`}>
                                    {getGradeFromRisk(aggregateMetrics?.riskScore || 0).pass ? '✓' : '🚨'}
                                </div>
                            </div>
                            <div className="bento-card p-8">
                                <p className="text-[0.625rem] font-black uppercase tracking-widest text-espresso/40 mb-2">Risk Exposure Score</p>
                                <div className="text-5xl font-black text-espresso mb-4">
                                    {aggregateMetrics?.riskScore || 0}<span className="text-lg opacity-20">/100</span>
                                </div>
                                <div className="h-2 bg-stone rounded-full overflow-hidden">
                                    <div 
                                        className="h-full bg-teal transition-all duration-1000" 
                                        style={{ width: `${aggregateMetrics?.riskScore || 0}%` }}
                                    ></div>
                                </div>
                            </div>
                            <div className="p-8 bg-teal rounded-[1.5rem] text-white border border-transparent shadow-xl flex flex-col justify-between">
                                <div>
                                    <p className="text-[0.625rem] font-black uppercase tracking-widest opacity-60 mb-2">Estimated Legal Liability</p>
                                    <div className="text-4xl font-black tracking-tighter drop-shadow-sm">
                                        ${(aggregateMetrics?.totalRiskCost || 0).toLocaleString()}
                                    </div>
                                </div>
                                <p className="text-[0.5625rem] font-black opacity-60 mt-4 uppercase tracking-widest bg-white/10 w-max px-3 py-1.5 rounded-full">Calculated via Unruh Act</p>
                            </div>
                        </div>

                        {/* Analytics Area */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 bg-stone/20 rounded-3xl p-6 border border-espresso/5 shadow-inner">
                            <div className="bg-white rounded-2xl p-6 border border-espresso/5 shadow-sm">
                                <h3 className="text-[0.625rem] font-black uppercase tracking-widest text-espresso/40 mb-2">Risk Score Trend</h3>
                                <div className="h-48 w-full">
                                    <RiskScoreTrendTremor data={trendData} height={192} />
                                </div>
                            </div>
                            <div className="bg-white rounded-2xl p-6 border border-espresso/5 shadow-sm">
                                <h3 className="text-[0.625rem] font-black uppercase tracking-widest text-espresso/40 mb-2">Violation Severity Trend</h3>
                                <div className="h-48 w-full">
                                    <SeverityTrendTremor data={trendData} height={192} />
                                </div>
                            </div>
                            <div className="bg-white rounded-2xl p-6 border border-espresso/5 shadow-sm lg:col-span-2">
                                <h3 className="text-[0.625rem] font-black uppercase tracking-widest text-espresso/40 mb-2">Top WCAG Violations</h3>
                                <div className="h-80 w-full">
                                    <WcagCriteriaBreakdownNivo data={criteriaBreakdown} />
                                </div>
                            </div>
                        </div>

                        {/* Tab Navigation */}
                        <div className="space-y-6 mt-8">
                            <div className="flex gap-6 border-b border-espresso/10 pb-4">
                                {['remediation', 'pages', 'reports', 'scans'].map(tab => (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveTab(tab)}
                                        className={`text-xs font-black uppercase tracking-widest transition-all pb-4 -mb-4.5 border-b-2 ${activeTab === tab ? 'text-teal border-teal' : 'text-espresso/40 hover:text-espresso border-transparent'}`}
                                    >
                                        {tab === 'remediation' ? 'ISSUES' : tab.replace(/([A-Z])/g, ' $1')}
                                    </button>
                                ))}
                            </div>

                            {activeTab === 'remediation' && (
                                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <div className="bg-white rounded-3xl border border-espresso/5 shadow-sm overflow-hidden">
                                        <div className="p-6 bg-stone/50 border-b border-espresso/5 flex flex-col md:flex-row gap-4 items-center justify-between">
                                            <div className="flex items-center gap-4 flex-1">
                                                <div className="relative flex-1 max-w-xs flex gap-2">
                                                    <input
                                                        type="text"
                                                        placeholder="SEARCH ISSUES..."
                                                        value={remediationSearch}
                                                        onChange={(e) => setRemediationSearch(e.target.value)}
                                                        className="w-full bg-white border border-espresso/10 rounded-xl px-4 py-2 text-[0.625rem] font-bold uppercase tracking-widest focus:outline-none focus:border-teal transition-all"
                                                    />
                                                    {/* Custom Severity Multi-Select */}
                                                    <div className="relative custom-dropdown">
                                                        <button 
                                                            onClick={() => {
                                                                setIsSeverityDropdownOpen(!isSeverityDropdownOpen);
                                                                setIsPolicyDropdownOpen(false);
                                                            }}
                                                            className="bg-white border border-espresso/10 rounded-xl px-4 py-2 text-[0.625rem] font-bold uppercase tracking-widest focus:outline-none focus:border-teal transition-all flex items-center gap-2 text-espresso/80 h-full"
                                                        >
                                                            {remediationSeverityFilter.length === 0 ? 'ANY SEVERITY' : `${remediationSeverityFilter.length} SEVERIT${remediationSeverityFilter.length === 1 ? 'Y' : 'IES'}`}
                                                            <span className="text-[0.5rem] opacity-50">▼</span>
                                                        </button>
                                                        {isSeverityDropdownOpen && (
                                                            <div className="absolute top-full mt-2 left-0 min-w-48 bg-white border border-espresso/10 rounded-xl shadow-xl z-50 p-2 flex flex-col gap-1">
                                                                {[
                                                                    { id: 'critical', label: 'CRITICAL' },
                                                                    { id: 'serious', label: 'SERIOUS' },
                                                                    { id: 'moderate', label: 'MODERATE' },
                                                                    { id: 'minor', label: 'MINOR' }
                                                                ].map(opt => (
                                                                    <label key={opt.id} className="flex items-center gap-2 p-2 hover:bg-stone/50 rounded-lg cursor-pointer transition-colors text-[0.625rem] font-bold uppercase tracking-widest text-espresso/80">
                                                                        <input 
                                                                            type="checkbox" 
                                                                            className="accent-teal w-4 h-4 cursor-pointer rounded"
                                                                            checked={remediationSeverityFilter.includes(opt.id)}
                                                                            onChange={(e) => {
                                                                                if (e.target.checked) {
                                                                                    setRemediationSeverityFilter([...remediationSeverityFilter, opt.id]);
                                                                                } else {
                                                                                    setRemediationSeverityFilter(remediationSeverityFilter.filter(s => s !== opt.id));
                                                                                }
                                                                            }}
                                                                        />
                                                                        {opt.label}
                                                                    </label>
                                                                ))}
                                                                {remediationSeverityFilter.length > 0 && (
                                                                    <button 
                                                                        onClick={() => setRemediationSeverityFilter([])}
                                                                        className="mt-2 text-[0.5625rem] font-black uppercase tracking-widest text-teal hover:underline text-center w-full pb-1"
                                                                    >
                                                                        Clear Filter
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Custom Policy Multi-Select */}
                                                    <div className="relative custom-dropdown">
                                                        <button 
                                                            onClick={() => {
                                                                setIsPolicyDropdownOpen(!isPolicyDropdownOpen);
                                                                setIsSeverityDropdownOpen(false);
                                                            }}
                                                            className="bg-white border border-espresso/10 rounded-xl px-4 py-2 text-[0.625rem] font-bold uppercase tracking-widest focus:outline-none focus:border-teal transition-all flex items-center gap-2 text-espresso/80 h-full"
                                                        >
                                                            {remediationPolicyFilter.length === 0 ? 'ANY POLICY' : `${remediationPolicyFilter.length} POLIC${remediationPolicyFilter.length === 1 ? 'Y' : 'IES'}`}
                                                            <span className="text-[0.5rem] opacity-50">▼</span>
                                                        </button>
                                                        {isPolicyDropdownOpen && (
                                                            <div className="absolute top-full mt-2 left-0 min-w-56 bg-white border border-espresso/10 rounded-xl shadow-xl z-50 p-2 flex flex-col gap-1">
                                                                {[
                                                                    { id: 'wcag2a', label: 'WCAG 2.0 A' },
                                                                    { id: 'wcag2aa', label: 'WCAG 2.0 AA' },
                                                                    { id: 'wcag21a', label: 'WCAG 2.1 A' },
                                                                    { id: 'wcag21aa', label: 'WCAG 2.1 AA' },
                                                                    { id: 'section508', label: 'SECTION 508' },
                                                                    { id: 'best-practice', label: 'BEST PRACTICE' }
                                                                ].map(opt => (
                                                                    <label key={opt.id} className="flex items-center gap-2 p-2 hover:bg-stone/50 rounded-lg cursor-pointer transition-colors text-[0.625rem] font-bold uppercase tracking-widest text-espresso/80">
                                                                        <input 
                                                                            type="checkbox" 
                                                                            className="accent-teal w-4 h-4 cursor-pointer rounded"
                                                                            checked={remediationPolicyFilter.includes(opt.id)}
                                                                            onChange={(e) => {
                                                                                if (e.target.checked) {
                                                                                    setRemediationPolicyFilter([...remediationPolicyFilter, opt.id]);
                                                                                } else {
                                                                                    setRemediationPolicyFilter(remediationPolicyFilter.filter(p => p !== opt.id));
                                                                                }
                                                                            }}
                                                                        />
                                                                        {opt.label}
                                                                    </label>
                                                                ))}
                                                                {remediationPolicyFilter.length > 0 && (
                                                                    <button 
                                                                        onClick={() => setRemediationPolicyFilter([])}
                                                                        className="mt-2 text-[0.5625rem] font-black uppercase tracking-widest text-teal hover:underline text-center w-full pb-1"
                                                                    >
                                                                        Clear Filter
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex gap-2 items-center">
                                                    {selectedIssues.size > 0 && (
                                                        <button 
                                                            onClick={() => handleTargetedRescan(Array.from(selectedIssues))}
                                                            className="bg-teal text-white px-4 py-2 rounded-xl text-[0.625rem] font-black uppercase tracking-widest hover:opacity-90 shadow-sm whitespace-nowrap border border-transparent transition-all hover:-translate-y-0.5"
                                                        >
                                                            ⚡ Target Verify {selectedIssues.size}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left border-collapse">
                                                <thead className="bg-stone/30 border-b border-espresso/5">
                                                    <tr>
                                                        <th className="p-5 w-12 text-center">
                                                            <input 
                                                                type="checkbox" 
                                                                className="w-4 h-4 accent-teal cursor-pointer"
                                                                // Toggle all logic
                                                                onChange={(e) => {
                                                                    const uniqueRuleIds = Array.from(new Set(filteredAndSortedIssues.map(v => v.ruleId)));
                                                                    if (selectedIssues.size === uniqueRuleIds.length && uniqueRuleIds.length > 0) {
                                                                        setSelectedIssues(new Set());
                                                                    } else {
                                                                        setSelectedIssues(new Set(uniqueRuleIds));
                                                                    }
                                                                }}
                                                                checked={(() => {
                                                                    const uniqueRuleIds = Array.from(new Set(filteredAndSortedIssues.map(v => v.ruleId)));
                                                                    return selectedIssues.size === uniqueRuleIds.length && uniqueRuleIds.length > 0;
                                                                })()}
                                                            />
                                                        </th>
                                                        <th 
                                                            onClick={() => setRemediationSort(prev => ({ key: 'impact', direction: prev.key === 'impact' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                                            className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40 cursor-pointer hover:bg-stone/50 transition-colors"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                Priority
                                                                {remediationSort.key === 'impact' && <span className="text-teal">{remediationSort.direction === 'asc' ? '↑' : '↓'}</span>}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            onClick={() => setRemediationSort(prev => ({ key: 'ruleId', direction: prev.key === 'ruleId' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                                            className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40 cursor-pointer hover:bg-stone/50 transition-colors"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                Issue Description
                                                                {remediationSort.key === 'ruleId' && <span className="text-teal">{remediationSort.direction === 'asc' ? '↑' : '↓'}</span>}
                                                            </div>
                                                        </th>
                                                        <th className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40">Policies</th>
                                                        <th className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40">Risk Cost</th>
                                                        <th 
                                                            onClick={() => setRemediationSort(prev => ({ key: 'instances', direction: prev.key === 'instances' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                                            className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40 text-center cursor-pointer hover:bg-stone/50 transition-colors"
                                                        >
                                                            <div className="flex items-center justify-center gap-2">
                                                                Instances
                                                                {remediationSort.key === 'instances' && <span className="text-teal">{remediationSort.direction === 'asc' ? '↑' : '↓'}</span>}
                                                            </div>
                                                        </th>
                                                        <th className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40 w-48 border-l border-espresso/5">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-espresso/5">
                                                    {filteredAndSortedIssues.length > 0 ? (
                                                        filteredAndSortedIssues.map((v, idx) => (
                                                            <tr key={v.ruleId + idx} className="hover:bg-teal/5 transition-colors group">
                                                                <td className="p-5 text-center">
                                                                    <input 
                                                                        type="checkbox"
                                                                        checked={selectedIssues.has(v.ruleId)}
                                                                        onChange={() => {
                                                                            const newSet = new Set(selectedIssues);
                                                                            if (newSet.has(v.ruleId)) newSet.delete(v.ruleId);
                                                                            else newSet.add(v.ruleId);
                                                                            setSelectedIssues(newSet);
                                                                        }}
                                                                        className="w-4 h-4 accent-teal cursor-pointer"
                                                                    />
                                                                </td>
                                                                <td className="p-5">
                                                                    <span className={`text-[0.5625rem] font-black border px-2 py-0.5 rounded uppercase tracking-widest ${
                                                                        v.impact === 'critical' ? 'border-(--fail-red)/30 text-(--fail-red) bg-(--fail-red)/10' :
                                                                        v.impact === 'serious' ? 'border-(--warning)/30 text-(--warning) bg-(--warning)/10' :
                                                                        'border-espresso/20 text-espresso/60'
                                                                    }`}>
                                                                        {v.impact}
                                                                    </span>
                                                                </td>
                                                                <td className="p-5">
                                                                    <div className="text-xs font-black text-espresso uppercase tracking-tight">{v.ruleId.replace(/-/g, ' ')}</div>
                                                                    <div className="text-[0.625rem] font-bold text-espresso/40 mt-1 max-w-sm" title={v.description}>{v.description}</div>
                                                                </td>
                                                                <td className="p-5">
                                                                    {v.tags?.length > 0 ? (
                                                                        <div className="flex flex-wrap gap-1 max-w-[12.5em]">
                                                                            {v.tags.filter(t => t.startsWith('wcag') || t === 'section508' || t === 'best-practice').map(t => (
                                                                                <span key={t} className="text-[0.5625rem] font-bold bg-stone/20 text-espresso/60 px-2 py-0.5 rounded-sm uppercase tracking-widest">
                                                                                    {t.replace('wcag', 'WCAG ').replace('2a', '2.0 A').replace('2aa', '2.0 AA').replace('21a', '2.1 A').replace('21aa', '2.1 AA').replace('section508', 'Sec 508').replace('best-practice', 'Best Practice')}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    ) : <span className="text-[0.5625rem] text-espresso/20 italic font-bold">NO POLICY</span>}
                                                                </td>
                                                                <td className="p-5">
                                                                    <span className="text-xs font-black text-espresso/80">
                                                                        ${((v.costPerInstance || 0) * (v.nodes?.length || 0)).toLocaleString()}
                                                                    </span>
                                                                </td>
                                                                <td className="p-5 text-center text-teal font-black text-xs">{v.nodes?.length || 0}</td>
                                                                <td className="p-5 border-l border-espresso/5 bg-stone/10">
                                                                    <div className="flex flex-col gap-2">
                                                                        <button 
                                                                            onClick={() => {
                                                                                const targetUrl = v.nodes[0]?.pageUrl || activeDomain;
                                                                                window.open(targetUrl, '_blank');
                                                                            }}
                                                                            className="text-[0.5625rem] text-center font-black text-espresso/80 hover:text-teal hover:border-teal uppercase tracking-widest border border-espresso/10 bg-white px-3 py-1 rounded-full w-full transition-colors cursor-pointer"
                                                                        >
                                                                            Open Page
                                                                        </button>
                                                                        <button 
                                                                            onClick={() => onIssueSelect && onIssueSelect(activeDomain, v.ruleId)}
                                                                            className="text-[0.5625rem] font-black text-white uppercase tracking-widest bg-teal px-3 py-1 rounded-full w-full hover:brightness-110 transition-all cursor-pointer shadow-sm"
                                                                        >
                                                                            Review Details
                                                                        </button>
                                                                        <button 
                                                                            onClick={() => handleTargetedRescan(v.ruleId)}
                                                                            className="text-[0.5625rem] font-black text-teal uppercase tracking-widest border border-teal bg-teal/5 px-3 py-1 rounded-full w-full hover:bg-teal hover:text-white transition-colors cursor-pointer"
                                                                        >
                                                                            Target Verify
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="7" className="p-20 text-center">
                                                                <div className="text-[0.625rem] font-black uppercase tracking-widest text-espresso/30 mb-2">No active backlog</div>
                                                                <div className="text-xs font-bold text-espresso/20 uppercase">Scan results are being hydrated or domain has no issues matching filters.</div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'scans' && (
                                <div className="bg-white rounded-3xl border border-espresso/5 shadow-sm">
                                    <div className="mx-6 mt-6 mb-2 p-3 bg-white border border-espresso/10 rounded-full flex flex-col md:flex-row gap-4 items-center justify-between shadow-sm">
                                        <div className="flex gap-2 items-center flex-wrap">
                                            <select
                                                value={historyDateRange}
                                                onChange={e => setHistoryDateRange(e.target.value)}
                                                className="px-4 py-2 rounded-full text-[0.5625rem] font-black uppercase tracking-widest transition-all border bg-white text-espresso border-espresso/20 outline-none cursor-pointer"
                                            >
                                                <option value="all">ALL TIME</option>
                                                <option value="7">LAST 7 DAYS</option>
                                                <option value="30">LAST 30 DAYS</option>
                                                <option value="90">LAST 90 DAYS</option>
                                                <option value="custom">CUSTOM RANGE</option>
                                            </select>
                                            {historyDateRange === 'custom' && (
                                                <div className="flex gap-2">
                                                    <input type="date" value={historyCustomStart} onChange={e => setHistoryCustomStart(e.target.value)} className="px-3 py-1.5 rounded-full text-[0.5625rem] font-black tracking-widest border border-espresso/20 outline-none h-8" />
                                                    <input type="date" value={historyCustomEnd} onChange={e => setHistoryCustomEnd(e.target.value)} className="px-3 py-1.5 rounded-full text-[0.5625rem] font-black tracking-widest border border-espresso/20 outline-none h-8" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="relative w-full md:flex-1 md:max-w-md">
                                            <input
                                                type="text"
                                                placeholder="FILTER HISTORY..."
                                                value={historySearch}
                                                onChange={(e) => setHistorySearch(e.target.value)}
                                                className="w-full bg-white border border-espresso/20 rounded-full px-5 py-2 text-[0.625rem] font-black uppercase tracking-[0.2em] focus:outline-none focus:border-teal transition-all placeholder-espresso/30 h-10"
                                            />
                                        </div>
                                    </div>
                                    <div className="overflow-x-auto mt-4">
                                        <table className="w-full text-left border-collapse">
                                            <thead className="bg-stone/30 border-y border-espresso/5">
                                                <tr>
                                                    <th className="p-5 w-12 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedScans.size === filteredAndSortedScans.length && filteredAndSortedScans.length > 0}
                                                            onChange={() => handleSelectAll(filteredAndSortedScans)}
                                                            className="w-4 h-4 cursor-pointer accent-teal"
                                                        />
                                                    </th>
                                                    <th 
                                                        onClick={() => setHistorySort(prev => ({ key: 'date', direction: prev.key === 'date' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                                        className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40 cursor-pointer hover:bg-stone/50 transition-colors"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            SCAN / DATE
                                                            {historySort.key === 'date' && <span className="text-teal">{historySort.direction === 'asc' ? '↑' : '↓'}</span>}
                                                        </div>
                                                    </th>
                                                    <th 
                                                        onClick={() => setHistorySort(prev => ({ key: 'score', direction: prev.key === 'score' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                                        className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40 cursor-pointer hover:bg-stone/50 transition-colors"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            RISK SCORE
                                                            {historySort.key === 'score' && <span className="text-teal">{historySort.direction === 'asc' ? '↑' : '↓'}</span>}
                                                        </div>
                                                    </th>
                                                    <th 
                                                        onClick={() => setHistorySort(prev => ({ key: 'violations', direction: prev.key === 'violations' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                                        className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40 text-center cursor-pointer hover:bg-stone/50 transition-colors"
                                                    >
                                                        <div className="flex items-center justify-center gap-2">
                                                            VIOLATIONS
                                                            {historySort.key === 'violations' && <span className="text-teal">{historySort.direction === 'asc' ? '↑' : '↓'}</span>}
                                                        </div>
                                                    </th>
                                                    <th className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40 w-48 border-l border-espresso/5">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-espresso/5">
                                                {filteredAndSortedScans.length > 0 ? (
                                                    filteredAndSortedScans.map(scan => renderScanRow(scan))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" className="p-20 text-center text-[0.625rem] font-black uppercase tracking-widest text-espresso/30">
                                                            No scans matching criteria
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'pages' && (
                                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <div className="bg-white rounded-3xl border border-espresso/5 shadow-sm overflow-hidden">
                                        <div className="p-6 bg-stone/50 border-b border-espresso/5 flex flex-col md:flex-row gap-4 items-center justify-between">
                                            <div className="flex items-center gap-4 flex-1">
                                                <div className="relative flex-1 max-w-xs flex flex-col gap-2">
                                                    <input
                                                        type="text"
                                                        placeholder="SEARCH PAGES..."
                                                        value={pagesSearchQuery}
                                                        onChange={(e) => setPagesSearchQuery(e.target.value)}
                                                        className="w-full bg-white border border-espresso/10 rounded-xl px-4 py-2 text-[0.625rem] font-bold uppercase tracking-widest focus:outline-none focus:border-teal transition-all"
                                                    />
                                                    <div className="flex gap-2 z-10 w-full mb-2">
                                                        {/* Custom Severity Multi-Select */}
                                                        <div className="relative custom-dropdown w-1/2">
                                                            <button 
                                                                onClick={() => {
                                                                    setIsPagesSeverityDropdownOpen(!isPagesSeverityDropdownOpen);
                                                                    setIsPagesPolicyDropdownOpen(false);
                                                                }}
                                                                className="w-full bg-white border border-espresso/10 rounded-xl px-4 py-2 text-[0.625rem] font-bold uppercase tracking-widest focus:outline-none focus:border-teal transition-all flex items-center justify-between text-espresso/80 h-10"
                                                            >
                                                                <span>{pagesSeverityFilter.length === 0 ? 'ANY SEVERITY' : `${pagesSeverityFilter.length} SEVERIT${pagesSeverityFilter.length === 1 ? 'Y' : 'IES'}`}</span>
                                                                <span className="text-[0.5rem] opacity-50">▼</span>
                                                            </button>
                                                            {isPagesSeverityDropdownOpen && (
                                                                <div className="absolute top-full mt-2 left-0 w-full min-w-[12.5em] bg-white border border-espresso/10 rounded-xl shadow-xl z-50 p-2 flex flex-col gap-1">
                                                                    {[
                                                                        { id: 'critical', label: 'CRITICAL' },
                                                                        { id: 'serious', label: 'SERIOUS' },
                                                                        { id: 'moderate', label: 'MODERATE' },
                                                                        { id: 'minor', label: 'MINOR' }
                                                                    ].map(opt => (
                                                                        <label key={opt.id} className="flex items-center gap-2 p-2 hover:bg-stone/50 rounded-lg cursor-pointer transition-colors text-[0.625rem] font-bold uppercase tracking-widest text-espresso/80">
                                                                            <input 
                                                                                type="checkbox" 
                                                                                className="accent-teal w-4 h-4 cursor-pointer rounded"
                                                                                checked={pagesSeverityFilter.includes(opt.id)}
                                                                                onChange={(e) => {
                                                                                    if (e.target.checked) setPagesSeverityFilter([...pagesSeverityFilter, opt.id]);
                                                                                    else setPagesSeverityFilter(pagesSeverityFilter.filter(s => s !== opt.id));
                                                                                }}
                                                                            />
                                                                            {opt.label}
                                                                        </label>
                                                                    ))}
                                                                    {pagesSeverityFilter.length > 0 && (
                                                                        <button 
                                                                            onClick={() => setPagesSeverityFilter([])}
                                                                            className="mt-2 text-[0.5625rem] font-black uppercase tracking-widest text-teal hover:underline text-center w-full pb-1"
                                                                        >
                                                                            Clear Filter
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Custom Policy Multi-Select */}
                                                        <div className="relative custom-dropdown w-1/2">
                                                            <button 
                                                                onClick={() => {
                                                                    setIsPagesPolicyDropdownOpen(!isPagesPolicyDropdownOpen);
                                                                    setIsPagesSeverityDropdownOpen(false);
                                                                }}
                                                                className="w-full bg-white border border-espresso/10 rounded-xl px-4 py-2 text-[0.625rem] font-bold uppercase tracking-widest focus:outline-none focus:border-teal transition-all flex items-center justify-between text-espresso/80 h-10"
                                                            >
                                                                <span>{pagesPolicyFilter.length === 0 ? 'ANY POLICY' : `${pagesPolicyFilter.length} POLIC${pagesPolicyFilter.length === 1 ? 'Y' : 'IES'}`}</span>
                                                                <span className="text-[0.5rem] opacity-50">▼</span>
                                                            </button>
                                                            {isPagesPolicyDropdownOpen && (
                                                                <div className="absolute top-full mt-2 left-0 w-full min-w-[12.5em] bg-white border border-espresso/10 rounded-xl shadow-xl z-50 p-2 flex flex-col gap-1">
                                                                    {[
                                                                        { id: 'wcag2a', label: 'WCAG 2.0 A' },
                                                                        { id: 'wcag2aa', label: 'WCAG 2.0 AA' },
                                                                        { id: 'wcag21a', label: 'WCAG 2.1 A' },
                                                                        { id: 'wcag21aa', label: 'WCAG 2.1 AA' },
                                                                        { id: 'section508', label: 'SECTION 508' },
                                                                        { id: 'best-practice', label: 'BEST PRACTICE' }
                                                                    ].map(opt => (
                                                                        <label key={opt.id} className="flex items-center gap-2 p-2 hover:bg-stone/50 rounded-lg cursor-pointer transition-colors text-[0.625rem] font-bold uppercase tracking-widest text-espresso/80">
                                                                            <input 
                                                                                type="checkbox" 
                                                                                className="accent-teal w-4 h-4 cursor-pointer rounded"
                                                                                checked={pagesPolicyFilter.includes(opt.id)}
                                                                                onChange={(e) => {
                                                                                    if (e.target.checked) setPagesPolicyFilter([...pagesPolicyFilter, opt.id]);
                                                                                    else setPagesPolicyFilter(pagesPolicyFilter.filter(p => p !== opt.id));
                                                                                }}
                                                                            />
                                                                            {opt.label}
                                                                        </label>
                                                                    ))}
                                                                    {pagesPolicyFilter.length > 0 && (
                                                                        <button 
                                                                            onClick={() => setPagesPolicyFilter([])}
                                                                            className="mt-2 text-[0.5625rem] font-black uppercase tracking-widest text-teal hover:underline text-center w-full pb-1"
                                                                        >
                                                                            Clear Filter
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {pagesRuleFilter && (
                                                        <div className="flex items-center gap-2 bg-teal/10 text-teal px-3 py-1.5 rounded-full text-[0.5625rem] font-black uppercase tracking-widest w-max self-start shadow-sm border border-teal/20">
                                                            <span>Rule: {pagesRuleFilter.replace(/-/g, ' ')}</span>
                                                            <button 
                                                                onClick={(e) => { e.stopPropagation(); setPagesRuleFilter(''); }} 
                                                                className="hover:text-(--fail-red) transition-colors flex items-center justify-center p-0.5"
                                                            >
                                                                ✕
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex gap-2 items-center">
                                                    {(pagesSearchQuery || pagesRuleFilter || pagesSeverityFilter.length > 0 || pagesPolicyFilter.length > 0) && (
                                                        <button
                                                            onClick={() => { 
                                                                setPagesSearchQuery(''); 
                                                                setPagesRuleFilter(''); 
                                                                setPagesSeverityFilter([]);
                                                                setPagesPolicyFilter([]);
                                                            }}
                                                            className="px-3 py-1.5 rounded-lg text-[0.5625rem] font-black uppercase tracking-widest transition-all bg-(--fail-red)/10 text-(--fail-red) hover:bg-(--fail-red)/20 mr-2 border border-(--fail-red)/20"
                                                        >
                                                            Reset
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="p-4 bg-stone/30 border-b border-espresso/5 flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <span className="text-[0.625rem] font-black uppercase tracking-widest text-espresso/60">
                                                    Showing {pagesData.length === 0 ? 0 : (pagesCurrentPage - 1) * (pagesPerPage === 'All' ? pagesData.length : pagesPerPage) + 1} to {pagesPerPage === 'All' ? pagesData.length : Math.min(pagesCurrentPage * pagesPerPage, pagesData.length)} of {pagesData.length} records
                                                </span>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[0.625rem] font-black uppercase tracking-widest text-espresso/40">Rows per page:</span>
                                                    <select 
                                                        value={pagesPerPage} 
                                                        onChange={(e) => {
                                                            setPagesPerPage(e.target.value === 'All' ? 'All' : Number(e.target.value));
                                                            setPagesCurrentPage(1);
                                                        }}
                                                        className="bg-white border border-espresso/10 rounded-lg px-2 py-1 text-[0.625rem] font-bold uppercase tracking-widest focus:outline-none focus:border-teal transition-all text-espresso/80 cursor-pointer"
                                                    >
                                                        <option value={10}>10</option>
                                                        <option value={25}>25</option>
                                                        <option value={50}>50</option>
                                                        <option value="All">All</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => setPagesCurrentPage(p => Math.max(1, p - 1))}
                                                    disabled={pagesCurrentPage === 1 || pagesData.length === 0}
                                                    className="px-3 py-1.5 rounded-lg text-[0.5625rem] font-black uppercase tracking-widest transition-all bg-white border border-espresso/10 text-espresso/60 hover:bg-stone/50 hover:text-espresso disabled:opacity-30 disabled:cursor-not-allowed"
                                                >
                                                    Prev
                                                </button>
                                                <button
                                                    onClick={() => setPagesCurrentPage(p => Math.min(Math.ceil(pagesData.length / pagesPerPage), p + 1))}
                                                    disabled={pagesPerPage === 'All' || pagesCurrentPage >= Math.ceil(pagesData.length / pagesPerPage) || pagesData.length === 0}
                                                    className="px-3 py-1.5 rounded-lg text-[0.5625rem] font-black uppercase tracking-widest transition-all bg-white border border-espresso/10 text-espresso/60 hover:bg-stone/50 hover:text-espresso disabled:opacity-30 disabled:cursor-not-allowed"
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left border-collapse">
                                                <thead className="bg-stone/30 border-b border-espresso/5">
                                                    <tr>
                                                        <th 
                                                            onClick={() => setPagesSort(prev => ({ key: 'riskScore', direction: prev.key === 'riskScore' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                                            className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40 text-center cursor-pointer hover:bg-stone/50 transition-colors"
                                                        >
                                                            <div className="flex items-center justify-center gap-2">
                                                                Status
                                                                {pagesSort.key === 'riskScore' && <span className="text-teal">{pagesSort.direction === 'asc' ? '↑' : '↓'}</span>}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            onClick={() => setPagesSort(prev => ({ key: 'url', direction: prev.key === 'url' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                                            className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40 w-[40%] cursor-pointer hover:bg-stone/50 transition-colors"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                URL
                                                                {pagesSort.key === 'url' && <span className="text-teal">{pagesSort.direction === 'asc' ? '↑' : '↓'}</span>}
                                                            </div>
                                                        </th>
                                                        <th className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40">Policies</th>
                                                        <th className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40">Risk Cost</th>
                                                        <th 
                                                            onClick={() => setPagesSort(prev => ({ key: 'totalViolations', direction: prev.key === 'totalViolations' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                                            className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40 text-center cursor-pointer hover:bg-stone/50 transition-colors"
                                                        >
                                                            <div className="flex items-center justify-center gap-2">
                                                                Issues
                                                                {pagesSort.key === 'totalViolations' && <span className="text-teal">{pagesSort.direction === 'asc' ? '↑' : '↓'}</span>}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            onClick={() => setPagesSort(prev => ({ key: 'lastScan', direction: prev.key === 'lastScan' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                                            className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40 cursor-pointer hover:bg-stone/50 transition-colors"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                Last Scanned
                                                                {pagesSort.key === 'lastScan' && <span className="text-teal">{pagesSort.direction === 'asc' ? '↑' : '↓'}</span>}
                                                            </div>
                                                        </th>
                                                        <th className="p-5 w-24 border-l border-espresso/5 font-black uppercase tracking-widest text-espresso/40 text-[0.5625rem] text-center">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-espresso/5">
                                                    {paginatedPages.length === 0 ? (
                                                        <tr><td colSpan="7" className="p-20 text-center text-[0.625rem] font-black uppercase tracking-widest text-espresso/30">No pages found</td></tr>
                                                    ) : (
                                                        paginatedPages.map((page, idx) => (
                                                            <tr key={page.url + idx} className="hover:bg-teal/5 transition-colors group">
                                                                <td className="p-5 text-center">
                                                                    <span className={`text-[0.5625rem] font-black border px-2 py-0.5 rounded ${page.riskScore > 0 ? 'border-(--fail-red)/30 text-(--fail-red) bg-(--fail-red)/10' : 'border-(--pass-green)/30 text-(--pass-green) bg-(--pass-green)/10'}`}>
                                                                        {page.riskScore === 0 ? 'CLEAN' : `SCORE: ${page.riskScore}`}
                                                                    </span>
                                                                </td>
                                                                <td className="p-5 text-xs font-bold text-espresso max-w-sm truncate" title={page.url}>{page.url}</td>
                                                                <td className="p-5">
                                                                    {page.tags?.length > 0 ? (
                                                                        <div className="flex flex-wrap gap-1 max-w-[9.375em]">
                                                                            {page.tags.filter(t => t.startsWith('wcag') || t === 'section508' || t === 'best-practice').map(t => (
                                                                                <span key={t} className="text-[0.5625rem] font-bold flex-shrink-0 bg-stone/20 text-espresso/60 px-2 py-0.5 rounded-sm uppercase tracking-widest">
                                                                                    {t.replace('wcag', 'WCAG ').replace('2a', '2.0 A').replace('2aa', '2.0 AA').replace('21a', '2.1 A').replace('21aa', '2.1 AA').replace('section508', 'Sec 508').replace('best-practice', 'Best Practice')}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    ) : <span className="text-[0.5625rem] text-espresso/20 italic font-bold">NO POLICY</span>}
                                                                </td>
                                                                <td className="p-5">
                                                                    <span className="text-xs font-black text-espresso/80">
                                                                        ${(page.totalRiskCost || 0).toLocaleString()}
                                                                    </span>
                                                                </td>
                                                                <td className="p-5 text-center text-teal font-black text-xs">{page.totalViolations || 0}</td>
                                                                <td className="p-5">
                                                                    <div className="text-[0.625rem] font-bold text-espresso/40">
                                                                        {page.lastScan ? (typeof page.lastScan === 'object' ? new Date(page.lastScan.seconds * 1000).toLocaleString() : new Date(page.lastScan * 1000).toLocaleString()) : 'Never'}
                                                                    </div>
                                                                </td>
                                                                <td className="p-5 border-l border-espresso/5 bg-stone/10">
                                                                    <div className="flex flex-col gap-2">
                                                                        <button 
                                                                            onClick={() => window.open(page.url, '_blank')}
                                                                            className="text-[0.5625rem] text-center font-black text-espresso/80 hover:text-teal hover:border-teal uppercase tracking-widest border border-espresso/10 bg-white px-3 py-1 rounded-full w-full transition-colors cursor-pointer"
                                                                        >
                                                                            Open Page
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'reports' && (
                                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <div className="bg-white rounded-3xl border border-espresso/5 shadow-sm overflow-hidden">
                                        <div className="p-6 bg-stone/50 border-b border-espresso/5 flex flex-col md:flex-row gap-4 items-center justify-between">
                                            <div className="flex items-center gap-4 flex-1">
                                                <input
                                                    type="text"
                                                    placeholder="SEARCH REPORTS..."
                                                    value={reportsSearch}
                                                    onChange={(e) => setReportsSearch(e.target.value)}
                                                    className="bg-white border border-espresso/10 rounded-xl px-4 py-2 text-[0.625rem] font-bold uppercase tracking-widest focus:outline-none focus:border-teal transition-all w-64 h-10"
                                                />
                                            </div>
                                            <button
                                                onClick={() => setIsGenerateReportModalOpen(true)}
                                                className="bg-teal text-white px-6 py-2 rounded-xl text-[0.625rem] font-black uppercase tracking-widest hover:opacity-90 transition-all shadow-md h-10 flex items-center gap-2"
                                            >
                                                <span>📊</span> NEW REPORT
                                            </button>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left border-collapse">
                                                <thead className="bg-stone/30 border-b border-espresso/5">
                                                    <tr>
                                                        <th 
                                                            className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40 cursor-pointer hover:bg-stone/50 transition-colors w-20 text-center"
                                                        >
                                                            Type
                                                        </th>
                                                        <th 
                                                            onClick={() => setReportsSort(prev => ({ key: 'name', direction: prev.key === 'name' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                                            className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40 cursor-pointer hover:bg-stone/50 transition-colors"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                REPORT NAME
                                                                {reportsSort.key === 'name' && <span className="text-teal">{reportsSort.direction === 'asc' ? '↑' : '↓'}</span>}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            onClick={() => setReportsSort(prev => ({ key: 'date', direction: prev.key === 'date' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                                            className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40 cursor-pointer hover:bg-stone/50 transition-colors"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                GENERATED DATE
                                                                {reportsSort.key === 'date' && <span className="text-teal">{reportsSort.direction === 'asc' ? '↑' : '↓'}</span>}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            onClick={() => setReportsSort(prev => ({ key: 'risk', direction: prev.key === 'risk' && prev.direction === 'desc' ? 'asc' : 'desc' }))}
                                                            className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40 cursor-pointer hover:bg-stone/50 transition-colors"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                RISK SCORE
                                                                {reportsSort.key === 'risk' && <span className="text-teal">{reportsSort.direction === 'asc' ? '↑' : '↓'}</span>}
                                                            </div>
                                                        </th>
                                                        <th className="p-5 text-[0.5625rem] font-black uppercase tracking-widest text-espresso/40 w-64 border-l border-espresso/5">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-espresso/5">
                                                    {sortedReports.length > 0 ? (
                                                        sortedReports.map(report => (
                                                            <tr key={report.id} className="hover:bg-teal/5 transition-colors group">
                                                                <td className="p-5 text-center">
                                                                    <div className="w-10 h-10 bg-teal/10 rounded-xl flex items-center justify-center text-lg mx-auto">📄</div>
                                                                </td>
                                                                <td className="p-5">
                                                                    <h4 className="text-sm font-black text-espresso uppercase tracking-tight">{report.name}</h4>
                                                                </td>
                                                                <td className="p-5">
                                                                    <div className="text-[0.625rem] font-bold text-espresso/60 uppercase tracking-widest">
                                                                        {report.createdAt ? new Date(report.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                                                                    </div>
                                                                </td>
                                                                <td className="p-5">
                                                                    <span className={`text-[0.625rem] font-black border px-2 py-0.5 rounded ${report.summary?.riskScore > 0 ? 'border-(--fail-red)/30 text-(--fail-red) bg-(--fail-red)/10' : 'border-(--pass-green)/30 text-(--pass-green) bg-(--pass-green)/10'}`}>
                                                                        {report.summary?.riskScore || 0}
                                                                    </span>
                                                                </td>
                                                                <td className="p-5 text-center border-l border-espresso/5 bg-stone/5 flex items-center justify-center gap-2">
                                                                    <button 
                                                                        onClick={() => setViewingReportId(report.id)} 
                                                                        className="text-[0.5625rem] font-black uppercase tracking-widest text-teal hover:bg-teal hover:text-white border border-teal bg-white px-3 py-1.5 rounded-full transition-all whitespace-nowrap"
                                                                    >
                                                                        View
                                                                    </button>
                                                                    <button 
                                                                        onClick={() => {
                                                                            if (!planLimits?.allowPdfExport) {
                                                                                setUpgradeModal({
                                                                                    isOpen: true,
                                                                                    title: 'PDF Export Locked',
                                                                                    message: 'Detailed PDF reports are an enterprise feature. Upgrade your plan to export professional accessibility reports.'
                                                                                });
                                                                                return;
                                                                            }
                                                                            handleDownloadPDF(report);
                                                                        }} 
                                                                        className={`text-[0.5625rem] font-black uppercase tracking-widest px-3 py-1.5 rounded-full transition-all whitespace-nowrap ${!planLimits?.allowPdfExport ? 'text-teal/50 border border-teal/20 bg-stone/5' : 'text-teal hover:bg-teal hover:text-white border border-teal bg-white'}`}
                                                                    >
                                                                        {downloadingPDF === report.id ? 'Wait...' : (!planLimits?.allowPdfExport ? '🔒 PDF' : 'PDF')}
                                                                    </button>
                                                                    <button 
                                                                        onClick={() => handleDeleteReport(report.id)} 
                                                                        className="text-[0.5625rem] font-black uppercase tracking-widest text-(--fail-red) hover:bg-(--fail-red) hover:text-white border border-(--fail-red) bg-white px-3 py-1.5 rounded-full transition-all whitespace-nowrap opacity-0 group-hover:opacity-100"
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="5" className="p-20 text-center text-[0.625rem] font-black uppercase tracking-widest text-espresso/30">
                                                                No reports found
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                ) : (
                    <div className="h-full flex items-center justify-center p-20 text-center">
                        <div className="max-w-md">
                            <div className="text-6xl mb-8 opacity-20">🌐</div>
                            <h2 className="text-2xl font-black text-espresso uppercase tracking-tighter mb-4">Intelligence Command</h2>
                            <p className="text-espresso/40 font-bold leading-relaxed">Select a domain from the left to view deep-dive compliance metrics, cross-scan history, and remediation tracking.</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            {showDeleteModal && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center bg-espresso/60 backdrop-blur-md">
                    <div className="bg-white p-10 rounded-3xl max-w-md w-full text-center animate-in zoom-in-95 duration-300 shadow-2xl">
                        <div className="w-16 h-16 bg-(--fail-red)/10 text-(--fail-red) rounded-full flex items-center justify-center mx-auto mb-6 text-2xl">⚠️</div>
                        <h3 className="text-xl font-black text-espresso uppercase tracking-tighter mb-4">Destroy Site Intelligence?</h3>
                        <p className="text-espresso/60 text-sm font-bold mb-8">This will permanently delete {activeDomain} along with all historical data.</p>
                        <div className="flex flex-col gap-3">
                            <button onClick={handleDeleteSite} className="w-full bg-(--fail-red) text-white py-4 rounded-2xl font-black uppercase tracking-widest text-xs hover:opacity-90 transition-all">Confirm Final Deletion</button>
                            <button onClick={() => setShowDeleteModal(false)} className="w-full bg-stone/20 text-espresso py-4 rounded-2xl font-black uppercase tracking-widest text-xs">Abort Operation</button>
                        </div>
                    </div>
                </div>
            )}

            {isGenerateReportModalOpen && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center bg-espresso/60 backdrop-blur-md">
                    <div className="bg-white p-10 rounded-3xl max-w-lg w-full animate-in zoom-in-95 duration-300 shadow-2xl">
                        <h3 className="text-xl font-black text-espresso uppercase tracking-tighter mb-8">Generate Audit Report</h3>
                        <div className="space-y-6">
                            <div>
                                <label className="block text-[0.625rem] font-black uppercase tracking-widest text-espresso/40 mb-2">Report Designation</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Q1 Accessibility Audit"
                                    value={newReportName}
                                    onChange={(e) => setNewReportName(e.target.value)}
                                    className="w-full bg-stone/20 border border-espresso/10 rounded-2xl px-6 py-4 text-sm font-bold text-espresso focus:outline-none focus:border-teal transition-all"
                                />
                            </div>
                            <button
                                onClick={handleGenerateReport}
                                disabled={isGeneratingReport || !newReportName.trim()}
                                className="w-full bg-teal text-white py-4 rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg hover:opacity-90 disabled:opacity-50 transition-all"
                            >
                                {isGeneratingReport ? "Compiling..." : "Authorize Generation"}
                            </button>
                            <button onClick={() => setIsGenerateReportModalOpen(false)} className="w-full bg-stone/20 text-espresso py-4 rounded-2xl font-black uppercase tracking-widest text-xs">Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {viewingReportId && (
                <div className="fixed inset-0 z-[120] bg-warm-pearl flex flex-col animate-in slide-in-from-bottom-10 duration-500">
                    <div className="h-20 bg-white border-b border-espresso/10 flex items-center justify-between px-10">
                        <button onClick={() => setViewingReportId(null)} className="w-10 h-10 rounded-full bg-stone/20 flex items-center justify-center hover:bg-stone/40">←</button>
                        <h2 className="text-lg font-black text-espresso uppercase tracking-tighter">Detailed Audit Findings</h2>
                        <button 
                            onClick={(e) => {
                                if (!planLimits?.allowPdfExport) {
                                    setUpgradeModal({
                                        isOpen: true,
                                        title: 'PDF Export Locked',
                                        message: 'Detailed PDF reports are an enterprise feature. Upgrade your plan to export professional accessibility reports.'
                                    });
                                    return;
                                }
                                const report = reports.find(r => r.id === viewingReportId);
                                if (report) handleDownloadPDF(report);
                            }}
                            className={`px-6 py-2 rounded-xl text-[0.625rem] font-black uppercase tracking-widest transition-all ${!planLimits?.allowPdfExport ? 'bg-stone/20 text-espresso/40 hover:bg-stone/30' : 'bg-teal text-white hover:opacity-90'}`}
                        >
                            {!planLimits?.allowPdfExport ? '🔒 Download PDF' : 'Download PDF'}
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-10">
                        <ScanResultsView 
                            results={reports.find(r => r.id === viewingReportId)?.hydratedResults || []} 
                            domain={activeDomain} 
                        />
                    </div>
                </div>
            )}
            {toast && (
                <Toast 
                    message={toast.message} 
                    type={toast.type} 
                    onClose={() => setToast(null)} 
                />
            )}
            
            <ConfirmModal
                isOpen={confirmState.isOpen}
                title={confirmState.title}
                message={confirmState.message}
                onConfirm={confirmState.onConfirm}
                onCancel={() => setConfirmState(prev => ({ ...prev, isOpen: false }))}
            />
            
            <UpgradePromptModal
                isOpen={upgradeModal.isOpen}
                onClose={() => setUpgradeModal({ isOpen: false, title: '', message: '' })}
                title={upgradeModal.title}
                message={upgradeModal.message}
            />
        </div>
    );
};

export default SitesPage;
